import {
	AppButton,
	Checkbox,
	ListItem,
	MenuListDivider,
	Pagination,
	Radio
} from '@clickguard/design-system'
import { RefundRequestDTO, RefundRequestStatusDTO } from 'api-models'
import ChevronRight from '@untitled-ui/icons-react/build/esm/ChevronRight'
import FilterLines from '@untitled-ui/icons-react/build/esm/FilterLines'
import RefreshCw01 from '@untitled-ui/icons-react/build/esm/RefreshCw01'
import moment from 'moment'
import { PlatformIcon } from 'components/platform-icon'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TERMINAL_STATUSES } from '../constants'

/** Figma card shadow (Shadows/xs), node 6669:161509. */
const CARD_SHADOW =
	'shadow-[0px_4px_16px_-2px_rgba(16,24,40,0.03),0px_1px_4px_-2px_rgba(16,24,40,0.02)]'

type TerminalStatus = 'submitted' | 'no_evidence' | 'failed'

const STATUS_META: Record<
	TerminalStatus,
	{ label: string; text: string; bg: string; border: string }
> = {
	submitted: {
		label: 'Submitted',
		text: 'var(--t-trend-up)',
		bg: 'var(--t-badge-active)',
		border: 'var(--t-badge-active-border)'
	},
	no_evidence: {
		label: 'No evidence',
		text: 'var(--t-desc-gray)',
		bg: 'var(--bg-plan)',
		border: 'var(--t-checkbox)'
	},
	failed: {
		label: 'Failed',
		text: 'var(--t-danger)',
		bg: 'var(--t-danger-warn)',
		border: 'var(--t-danger-warn-border)'
	}
}

/** Purple count pill in the Filters left-pane rows (Figma "Count badge"). */
const CountBadge = ({ count }: { count: number }) => (
	<span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-[4px] bg-brand px-1.5 text-[12px] font-semibold leading-4 text-white">
		{count}
	</span>
)

const StatusPill = ({ status }: { status: RefundRequestStatusDTO }) => {
	const meta = STATUS_META[status as TerminalStatus]
	if (!meta) return null
	return (
		<span
			className="inline-flex px-2.5 py-0.5 rounded-full border text-[12px] leading-[18px] font-medium"
			style={{
				color: meta.text,
				backgroundColor: meta.bg,
				borderColor: meta.border
			}}
		>
			{meta.label}
		</span>
	)
}

interface SubmissionHistoryTableProps {
	items: RefundRequestDTO[]
	availableMonths: string[]
	accountName: string
	onRefresh: () => void
	refreshing?: boolean
	onView: (item: RefundRequestDTO) => void
}

const COLUMNS = [
	'Account',
	'Submission date',
	'Date range',
	'Submission status',
	'Invalid Clicks Reported'
]

/** §5 Submission history table — Figma node 6669:161509 (outer #f6f6f6 card →
 *  title bar → inner white card with Filters/Refresh, data grid, pagination). */
export const SubmissionHistoryTable = ({
	items,
	availableMonths,
	accountName,
	onRefresh,
	refreshing,
	onView
}: SubmissionHistoryTableProps) => {
	const [statusFilter, setStatusFilter] = useState<Set<TerminalStatus>>(
		new Set()
	)
	const [monthFilter, setMonthFilter] = useState<string | null>(null)
	const [filterCategory, setFilterCategory] = useState<'status' | 'month'>(
		'status'
	)
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(10)
	const [filtersOpen, setFiltersOpen] = useState(false)
	const filtersRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!filtersOpen) return
		const onClick = (e: MouseEvent) => {
			if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
				setFiltersOpen(false)
			}
		}
		document.addEventListener('mousedown', onClick)
		return () => document.removeEventListener('mousedown', onClick)
	}, [filtersOpen])

	const filtered = useMemo(() => {
		return items
			.filter((i) =>
				(TERMINAL_STATUSES as readonly string[]).includes(i.status)
			)
			.filter((i) =>
				statusFilter.size === 0
					? true
					: statusFilter.has(i.status as TerminalStatus)
			)
			.filter((i) =>
				monthFilter
					? i.submission_date &&
						moment(i.submission_date).format('YYYY-MM') === monthFilter
					: true
			)
	}, [items, statusFilter, monthFilter])

	const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
	const safePage = Math.min(page, totalPages)
	const pageItems = filtered.slice(
		(safePage - 1) * pageSize,
		safePage * pageSize
	)

	const toggleStatus = (s: TerminalStatus) => {
		setStatusFilter((prev) => {
			const next = new Set(prev)
			next.has(s) ? next.delete(s) : next.add(s)
			return next
		})
		setPage(1)
	}

	const clearFilters = () => {
		setStatusFilter(new Set())
		setMonthFilter(null)
		setPage(1)
	}

	const activeFilterCount = statusFilter.size + (monthFilter ? 1 : 0)

	return (
		<div
			className={`flex flex-col overflow-hidden rounded-[12px] border border-t-checkbox bg-table-band ${CARD_SHADOW}`}
		>
			<div className="px-4 py-3">
				<h3 className="text-[16px] leading-[22px] font-medium text-t-coupon">
					Submission history
				</h3>
			</div>

			<div
				className={`flex flex-col gap-3 overflow-hidden rounded-[12px] border border-t-checkbox bg-white p-3 ${CARD_SHADOW}`}
			>
				<div className="flex items-center justify-between">
					<div className="relative" ref={filtersRef}>
						<AppButton
							variant="secondary"
							roundness="default"
							size="md"
							leftIcon={<FilterLines className="h-4 w-4" />}
							label={
								activeFilterCount > 0
									? `Filters (${activeFilterCount})`
									: 'Filters'
							}
							onClick={() => setFiltersOpen((o) => !o)}
							selected={filtersOpen}
						/>
						{filtersOpen && (
							<div className="absolute left-0 mt-1 z-50 flex items-start overflow-hidden rounded-[12px] border border-t-traffic bg-white shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)]">
								{/* Left pane — filter categories (Figma 6669:161040). */}
								<div className="flex w-[224px] flex-col gap-0.5 self-stretch border-r border-t-traffic bg-table-band-subtle p-2">
									<ListItem
										label="Submission status"
										selected={filterCategory === 'status'}
										badge={
											statusFilter.size > 0 ? (
												<CountBadge count={statusFilter.size} />
											) : undefined
										}
										trailingIcon={
											<ChevronRight className="h-4 w-4 text-t-gray-hold" />
										}
										onClick={() => setFilterCategory('status')}
									/>
									<ListItem
										label="Submission month"
										selected={filterCategory === 'month'}
										badge={monthFilter ? <CountBadge count={1} /> : undefined}
										trailingIcon={
											<ChevronRight className="h-4 w-4 text-t-gray-hold" />
										}
										onClick={() => setFilterCategory('month')}
									/>
									<MenuListDivider />
									<ListItem
										weight="medium"
										label={<span className="text-brand-tint-text">Clear filters</span>}
										onClick={clearFilters}
									/>
								</div>

								{/* Right pane — options for the selected category (Figma 6669:161047). */}
								<div className="flex max-h-[300px] w-[252px] flex-col gap-0.5 overflow-y-auto p-2">
									{filterCategory === 'status' ? (
										(
											['submitted', 'no_evidence', 'failed'] as TerminalStatus[]
										).map((s) => (
											<ListItem
												key={s}
												label={STATUS_META[s].label}
												leadingControl={
													<Checkbox
														size="sm"
														checked={statusFilter.has(s)}
														readOnly
														tabIndex={-1}
														className="pointer-events-none"
													/>
												}
												onClick={() => toggleStatus(s)}
											/>
										))
									) : availableMonths.length > 0 ? (
										availableMonths.map((m) => (
											<ListItem
												key={m}
												label={moment(m, 'YYYY-MM').format('MMMM YYYY')}
												leadingControl={
													<Radio
														size="sm"
														checked={monthFilter === m}
														readOnly
														tabIndex={-1}
														className="pointer-events-none"
													/>
												}
												onClick={() => {
													setMonthFilter(m)
													setPage(1)
												}}
											/>
										))
									) : (
										<p className="px-2 py-3 text-[13px] text-t-gray-hold">
											No months available
										</p>
									)}
								</div>
							</div>
						)}
					</div>

					<AppButton
						loader="dots"
						variant="secondary"
						roundness="default"
						size="md"
						leftIcon={<RefreshCw01 className="h-4 w-4" />}
						label="Refresh"
						isLoading={refreshing}
						onClick={onRefresh}
					/>
				</div>

				<div className="overflow-x-auto rounded-[8px]">
					<table className="w-full table-fixed text-left">
						<thead>
							<tr className="bg-table-band">
								{COLUMNS.map((c) => (
									<th
										key={c}
										className="h-11 px-6 text-[12px] leading-4 font-semibold text-t-desc-gray whitespace-nowrap border-b border-t-checkbox"
									>
										{c}
									</th>
								))}
								<th
									aria-hidden
									className="h-11 w-[131px] border-b border-t-checkbox"
								/>
							</tr>
						</thead>
						<tbody>
							{pageItems.length === 0 ? (
								<tr>
									<td
										colSpan={COLUMNS.length + 1}
										className="h-[72px] px-6 text-center text-[14px] text-t-gray-hold"
									>
										No refund requests match your filters
									</td>
								</tr>
							) : (
								pageItems.map((item) => (
									<tr key={item.id} className="border-b border-t-traffic">
										<td className="h-[72px] px-6">
											<div className="flex items-center gap-3">
												<PlatformIcon
													platform="GADS"
													className="h-6 w-6 shrink-0"
												/>
												<div className="min-w-0">
													<p className="text-[14px] leading-[18px] text-t-coupon truncate">
														{accountName}
													</p>
													<p className="text-[12px] leading-4 text-t-gray-hold">
														{item.customer_id}
													</p>
												</div>
											</div>
										</td>
										<td className="h-[72px] px-6 text-[14px] leading-[18px] text-t-coupon whitespace-nowrap">
											{item.submission_date
												? moment(item.submission_date).format('MMM D, YYYY')
												: '–'}
										</td>
										<td className="h-[72px] px-6 text-[14px] leading-[18px] text-t-coupon">
											{moment(item.date_range_start).format('MMM D, YYYY')} –{' '}
											{moment(item.date_range_end).format('MMM D, YYYY')}
										</td>
										<td className="h-[72px] px-6 whitespace-nowrap">
											<StatusPill status={item.status} />
										</td>
										<td className="h-[72px] px-6 text-[14px] leading-[18px] text-t-coupon whitespace-nowrap">
											{item.invalid_click_count.toLocaleString()}
										</td>
										<td className="h-[72px] px-6 text-right whitespace-nowrap">
											<AppButton
												variant="secondary"
												roundness="default"
												size="sm"
												rightIcon={<ChevronRight className="h-4 w-4" />}
												label="View"
												onClick={() => onView(item)}
											/>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>

				{filtered.length > 0 && (
					<Pagination
						page={safePage}
						totalPages={totalPages}
						pageSize={pageSize}
						pageSizeOptions={[10, 25, 50]}
						onPageChange={setPage}
						onPageSizeChange={(s) => {
							setPageSize(s)
							setPage(1)
						}}
					/>
				)}
			</div>
		</div>
	)
}
